import {inject, Injectable, NgZone} from '@angular/core';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import * as moment from 'moment';
import {MenusPlanning2Service} from './menus-planning2.service';
import {MenusCompositionService} from './menus-composition.service';
import {ToastService} from '../technique/toast.service';
import {CelluleTableauDTO} from '../../dtos/gestionmenus/planning/cellule-tableau-dto';
import {MenuCompositionDTO} from '../../dtos/menucomposition-dto';
import {MSG_KEY, MSG_SEVERITY} from '../../constants';
import {UtilsService} from "../../utils/utils.service";

@Injectable({ providedIn: 'root' })
export class MenuDragdropService {

  #mp2Svc = inject(MenusPlanning2Service);
  #menusCompositionSvc = inject(MenusCompositionService);
  #utils = inject(UtilsService);
  #toastSvc = inject(ToastService);

  isCopyMode = false;

  constructor() {
    inject(NgZone).runOutsideAngular(() => {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Control') {
          this.isCopyMode = true;
          document.body.classList.add('cdk-copy-mode');
        }
      });
      document.addEventListener('keyup', (e) => {
        if (e.key === 'Control') {
          this.isCopyMode = false;
          document.body.classList.remove('cdk-copy-mode');
        }
      });
    });
  }

  duplicateMenuCompositionOnTable(
    event: CdkDragDrop<any[]>,
    targetCellule: CelluleTableauDTO
  ): { sourceCellule: CelluleTableauDTO; targetCellule: CelluleTableauDTO; slots: any[] } | null {
    const data = event.item.data as { menuComposition: MenuCompositionDTO; cellule: CelluleTableauDTO };
    const sourceCellule = data?.cellule;
    const menuComposition = data?.menuComposition;

    if (!sourceCellule || !targetCellule || !menuComposition || targetCellule.nonModifiable) {
      return null;
    }

    const slots = this.#mp2Svc.getSlots(targetCellule);
    const targetMap: any = targetCellule.mapMenuComposition as any;

    const clampedIndex = Math.min(event.currentIndex, slots.length - 1);
    const targetSlot = slots[clampedIndex];
    if (!targetSlot) {
      return null;
    }
    const targetIdxSlot = targetSlot.idxSlot;

    if (!this.#utils.isNullOrEmpty(targetMap[targetIdxSlot])) {
      return null;
    }

    const clone: MenuCompositionDTO = JSON.parse(JSON.stringify(menuComposition));
    clone.id = null;
    clone.ordre = targetIdxSlot;
    clone.cmcdId = targetCellule.idContratMenuConviveDecoupage;
    clone.repasLibelle = targetCellule.libelleRepas;
    clone.decoupageRepasLibelle = targetCellule.libelleDecoupageRepas;
    clone.menuDecoupageDate = clone.menuDecoupageDate ?? ({} as any);
    clone.menuDecoupageDate.id = targetCellule.idMenuDecoupageDate;
    clone.menuDecoupageDate.dateMenu = moment(targetCellule.dateMenu).toDate();

    targetMap[targetIdxSlot] = clone;
    targetCellule.mapMenuComposition = { ...targetMap };

    const refreshedTarget = this.recompute(targetCellule);
    this.updateSlots(refreshedTarget);
    this.persistDuplication(clone, refreshedTarget, targetIdxSlot);

    return {
      sourceCellule,
      targetCellule: refreshedTarget,
      slots: this.#mp2Svc.getSlots(refreshedTarget)
    };
  }

  moveMenuCompositionOnTable(
    event: CdkDragDrop<any[]>,
    targetCellule: CelluleTableauDTO
  ): { sourceCellule: CelluleTableauDTO; targetCellule: CelluleTableauDTO; slots: any[] } | null {

    const data = event.item.data as { menuComposition: MenuCompositionDTO; cellule: CelluleTableauDTO };
    const sourceCellule = data?.cellule;
    const menuComposition = data?.menuComposition;

    if (
      (!sourceCellule || !targetCellule || !menuComposition)
      || (event.previousContainer === event.container && event.previousIndex === event.currentIndex)
      || this.#utils.isNullOrEmpty(menuComposition.id)
      || sourceCellule.nonModifiable
      || targetCellule.nonModifiable) {
      return null;
    }

    if (event.previousContainer === event.container) {
      return this.handleIntraCelluleReorder(event, targetCellule);
    }

    return this.handleInterCelluleMove(event, sourceCellule, targetCellule, menuComposition);
  }

  private handleIntraCelluleReorder(
    event: CdkDragDrop<any[]>,
    targetCellule: CelluleTableauDTO
  ): { sourceCellule: CelluleTableauDTO; targetCellule: CelluleTableauDTO; slots: any[] } {

    const targetMap: any = targetCellule.mapMenuComposition as any;
    const slots = this.#mp2Svc.getSlots(targetCellule);
    const sourceSlot = slots[event.previousIndex]?.idxSlot;
    const targetSlot = slots[event.currentIndex]?.idxSlot;

    if (!sourceSlot || !targetSlot || sourceSlot === targetSlot) {
      return {
        sourceCellule: targetCellule,
        targetCellule,
        slots: this.#mp2Svc.getSlots(targetCellule)
      };
    }

    const sourceMenuComposition = targetMap[sourceSlot];
    const targetMenuComposition = targetMap[targetSlot];

    if (!sourceMenuComposition) {
      return {
        sourceCellule: targetCellule,
        targetCellule,
        slots: this.#mp2Svc.getSlots(targetCellule)
      };
    }

    if (targetMenuComposition) {
      targetMap[sourceSlot] = targetMenuComposition;
      targetMap[targetSlot] = sourceMenuComposition;
      targetMap[sourceSlot].ordre = sourceSlot;
      targetMap[targetSlot].ordre = targetSlot;
    } else {
      delete targetMap[sourceSlot];
      targetMap[targetSlot] = sourceMenuComposition;
      targetMap[targetSlot].ordre = targetSlot;
    }

    targetCellule.mapMenuComposition = { ...targetMap };
    const refreshed = this.recompute(targetCellule);
    this.updateSlots(refreshed);
    this.persistCellules(refreshed);

    return { sourceCellule: refreshed, targetCellule: refreshed, slots: this.#mp2Svc.getSlots(refreshed) };
  }

  private handleInterCelluleMove(
    event: CdkDragDrop<any[]>,
    sourceCellule: CelluleTableauDTO,
    targetCellule: CelluleTableauDTO,
    menuComposition: MenuCompositionDTO
  ): { sourceCellule: CelluleTableauDTO; targetCellule: CelluleTableauDTO; slots: any[] } | null {

    const targetMap: any = targetCellule.mapMenuComposition as any;
    const sourceMap: any = sourceCellule.mapMenuComposition as any;
    const slots = this.#mp2Svc.getSlots(targetCellule);

    const sourceOrdreKey = Object.keys(sourceMap).find(key => sourceMap[key]?.id === menuComposition.id);
    if (sourceOrdreKey == null) {
      return null;
    }
    const sourceOrdre = +sourceOrdreKey;

    const clampedIndex = Math.min(event.currentIndex, slots.length - 1);
    const targetSlot = slots[clampedIndex];
    if (!targetSlot) {
      return null;
    }
    const targetIdxSlot = targetSlot.idxSlot;

    if (!this.#utils.isNullOrEmpty(targetMap[targetIdxSlot])) {
      return this.handlePermutation(sourceCellule, targetCellule, sourceMap, targetMap, sourceOrdre, targetIdxSlot, menuComposition);
    }

    return this.handleMove(sourceCellule, targetCellule, sourceMap, targetMap, sourceOrdre, targetIdxSlot, menuComposition);
  }

  private handlePermutation(
    sourceCellule: CelluleTableauDTO,
    targetCellule: CelluleTableauDTO,
    sourceMap: any,
    targetMap: any,
    sourceOrdre: number,
    targetIdxSlot: number,
    menuComposition: MenuCompositionDTO
  ): { sourceCellule: CelluleTableauDTO; targetCellule: CelluleTableauDTO; slots: any[] } {

    const targetMc = targetMap[targetIdxSlot];
    sourceMap[sourceOrdre] = targetMc;
    targetMap[targetIdxSlot] = menuComposition;
    sourceCellule.mapMenuComposition = { ...sourceMap };
    targetCellule.mapMenuComposition = { ...targetMap };

    const refreshedSource = this.recompute(sourceCellule);
    const refreshedTarget = this.recompute(targetCellule);

    this.updateSlots(refreshedSource);
    this.updateSlots(refreshedTarget);
    this.persistPermutation(menuComposition, targetMc, sourceCellule, sourceOrdre, targetCellule, targetIdxSlot);

    return {
      sourceCellule: refreshedSource,
      targetCellule: refreshedTarget,
      slots: this.#mp2Svc.getSlots(refreshedTarget)
    };
  }

  private handleMove(
    sourceCellule: CelluleTableauDTO,
    targetCellule: CelluleTableauDTO,
    sourceMap: any,
    targetMap: any,
    sourceOrdre: number,
    targetIdxSlot: number,
    menuComposition: MenuCompositionDTO
  ): { sourceCellule: CelluleTableauDTO; targetCellule: CelluleTableauDTO; slots: any[] } {

    delete sourceMap[sourceOrdre];

    sourceCellule.mapMenuComposition = this.compact(sourceMap);
    menuComposition.ordre = targetIdxSlot;
    menuComposition.cmcdId = targetCellule.idContratMenuConviveDecoupage;
    menuComposition.repasLibelle = targetCellule.libelleRepas;
    menuComposition.decoupageRepasLibelle = targetCellule.libelleDecoupageRepas;
    menuComposition.menuDecoupageDate = menuComposition.menuDecoupageDate ?? ({} as any);
    menuComposition.menuDecoupageDate.id = targetCellule.idMenuDecoupageDate;
    menuComposition.menuDecoupageDate.dateMenu = moment(targetCellule.dateMenu).toDate();

    targetMap[targetIdxSlot] = menuComposition;
    targetCellule.mapMenuComposition = { ...targetMap };

    const refreshedSource = this.recompute(sourceCellule);
    const refreshedTarget = this.recompute(targetCellule);

    this.updateSlots(refreshedSource);
    this.updateSlots(refreshedTarget);
    this.persistCellules(refreshedSource, refreshedTarget);

    return {
      sourceCellule: refreshedSource,
      targetCellule: refreshedTarget,
      slots: this.#mp2Svc.getSlots(refreshedTarget)
    };
  }

  private recompute(cellule: CelluleTableauDTO): CelluleTableauDTO {
    this.#mp2Svc.calculCoutUnitairePondere(cellule);
    this.#mp2Svc.calculCoutUnitaireTotal(cellule);
    this.#mp2Svc.computeTauxDePriseDecoupageRepas(cellule);
    return cellule;
  }

  private compact(map: any): any {
    const nextMap: any = {};
    let idx = 1;
    Object.keys(map).sort((a, b) => +a - +b).forEach(key => {
      const mc = map[key];
      if (mc) {
        mc.ordre = idx;
        nextMap[idx++] = mc;
      }
    });
    return nextMap;
  }

  private updateSlots(cellule: CelluleTableauDTO): void {
    cellule.changeDetector?.markForCheck();
    cellule.updateSlots?.(this.#mp2Svc.getSlots(cellule));
    this.#mp2Svc.planningMenus.changeDetector?.markForCheck();
  }

  private persistPermutation(
    sourceMc: MenuCompositionDTO, targetMc: MenuCompositionDTO,
    sourceCellule: CelluleTableauDTO, sourceOrdre: number,
    targetCellule: CelluleTableauDTO, targetIdxSlot: number
  ): void {
    this.#menusCompositionSvc.permuteMenusCompositions(sourceMc, targetMc).subscribe(response => {
      if (this.#utils.isResponseSupplierError(response)) {
        this.#toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.WARNING, `La permutation n'a pas pu être réalisé.`);
      } else {
        const updated = response.resultList;
        if (updated?.length >= 2) {
          (sourceCellule.mapMenuComposition as any)[sourceOrdre] = updated[0];
          (targetCellule.mapMenuComposition as any)[targetIdxSlot] = updated[1];
          this.recompute(sourceCellule);
          this.recompute(targetCellule);
          this.updateSlots(sourceCellule);
          this.updateSlots(targetCellule);
        }
        this.#toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Les deux plats ont été permutés avec succès ainsi que les régimes annexes`);
      }
    });
  }

  private persistDuplication(clone: MenuCompositionDTO, targetCellule: CelluleTableauDTO, targetIdxSlot: number): void {
    this.#menusCompositionSvc.saveMenusCompositions([clone]).subscribe(response => {
      if (this.#utils.isResponseSupplierError(response)) {
        this.#toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.WARNING, `La duplication n'a pas pu être réalisée.`);
        delete (targetCellule.mapMenuComposition as any)[targetIdxSlot];
        this.recompute(targetCellule);
        this.updateSlots(targetCellule);
      } else {
        const saved = response.resultList?.[0];
        if (saved) {
          (targetCellule.mapMenuComposition as any)[targetIdxSlot] = saved;
          this.recompute(targetCellule);
          this.updateSlots(targetCellule);
        }
        this.#toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Le plat a été dupliqué avec succès.`);
      }
    });
  }

  private persistCellules(...celluleList: CelluleTableauDTO[]): void {
    const menuMap: Record<number, MenuCompositionDTO> = {};

    celluleList.forEach(cellule => {
      if (!cellule?.mapMenuComposition) return;
      Object.keys(cellule.mapMenuComposition).forEach(key => {
        const mc = cellule.mapMenuComposition[key];
        if (!this.#utils.isNullOrEmpty(mc) && !this.#utils.isNullOrEmpty(mc.id)) {
          menuMap[mc.id] = mc;
        }
      });
    });

    const menuCompoList = Object.values(menuMap);
    if (this.#utils.isCollectionNullOrEmpty(menuCompoList)) {
      return;
    }

    this.#menusCompositionSvc.saveMenusCompositions(menuCompoList).subscribe(response => {
      if (this.#utils.isResponseSupplierError(response)) {
        this.#toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.WARNING, `La sauvegarde du déplacement n'a pas pu être réalisé.`);
      }
      else {
        this.#toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Le plat a été déplacé avec succès`);
      }
    });
  }
}
